/**
 * ArtifactMessage — the assistant's turn when the answer is a document.
 *
 * The chat stays a conversation and the pane holds the document, so the turn
 * reads as a message with a document in it. Every conversational word here is
 * written by the MODEL and arrives on the envelope (`leadIn`, `closing`,
 * `followUp`) in the session's output language.
 *
 * There is deliberately NO fallback copy. A canned English sentence would be
 * wrong in a Norwegian session and reads as canned in any language, so when
 * the model declines a line we render nothing in its place: just the card.
 */

import React from 'react';
import { ResearchIntent } from '../types';
import ArtifactCard from './ArtifactCard';

interface Props {
  intent: ResearchIntent;
  title: string;
  preview: string;
  isStreaming?: boolean;
  isOpen?: boolean;
  version?: number;
  versionCount?: number;
  /** Model-written opening line. Empty when the model gave none. */
  leadIn?: string;
  /** Model-written closing line. Empty when the model gave none. */
  closing?: string;
  /** Model-written suggested next questions. */
  followUp?: string[];
  onOpen: () => void;
  onFollowUp?: (text: string) => void;
}

const ArtifactMessage: React.FC<Props> = ({
  intent,
  title,
  preview,
  isStreaming = false,
  isOpen = false,
  version,
  versionCount,
  leadIn = '',
  closing = '',
  followUp = [],
  onOpen,
  onFollowUp,
}) => (
  <div data-testid="artifact-message">
    {leadIn.trim() && (
      <p className="text-sm leading-relaxed text-fg-body mb-2" data-testid="artifact-lead-in">
        {leadIn}
      </p>
    )}

    <ArtifactCard
      intent={intent}
      title={title}
      preview={preview}
      isStreaming={isStreaming}
      isOpen={isOpen}
      version={version}
      versionCount={versionCount}
      onOpen={onOpen}
    />

    {closing.trim() && (
      <p className="mt-2 text-sm leading-relaxed text-fg-body" data-testid="artifact-closing">
        {closing}
      </p>
    )}

    {followUp.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="artifact-followups">
        {followUp.slice(0, 4).map((question) => (
          <button
            key={question}
            type="button"
            onClick={() => onFollowUp?.(question)}
            disabled={!onFollowUp}
            data-testid="artifact-followup"
            className="px-2.5 py-1 rounded-full text-[11px] text-left
                       bg-surface-sunken border border-hairline-soft text-fg-body
                       hover:border-champagne/50 hover:text-fg-strong
                       disabled:opacity-50 transition-colors duration-200 ease-slide"
          >
            {question}
          </button>
        ))}
      </div>
    )}
  </div>
);

export default ArtifactMessage;
